import mixpanel from 'mixpanel-browser';
import { createAction, handleActions } from 'redux-actions';
import momenttz from 'moment-timezone';
import Agent from '../../api/Agent';
import { handleError } from '../user';
import constants from '../../services/constants';
import formatDate from '../../services/formatDate';
import mixpanelConstants from '../../constants/mixpanel';
import timesheetConstants from '../../constants/timesheetStatus';
import { setOpenAlertModal, setAlertMessageInfo } from '../supervisor/supervisordashboard';
import dayjs from 'dayjs';

export const setSuperTimesheetDetail = createAction('SET_SUPER_TIMESHEET_DETAIL');
export const setComments = createAction('SET_COMMENTS');
export const setMessage = createAction('SET_MESSAGE');
export const clearMessage = createAction('CLEAR_MESSAGES');
export const setShiftSummaryTimesheet = createAction('SET_SHIFT_TIMESHEET_SUMMARY');
export const setShiftBreaktimes = createAction('SET_SHIFT_BREAKTIMES');
export const setBreakDuration = createAction('SET_BREAK_DURATION');
export const setBreakDateTime = createAction('SET_BREAK_DATETIME');
export const setAdjCheckInTime = createAction('SET_ADJ_CHECKINTIME');
export const setAdjCheckOutTime = createAction('SET_ADJ_CHECKOUTTIME');
export const setAdjCheckInDate = createAction('SET_ADJ_CHECKINDATE');
export const setAdjCheckOutDate = createAction('SET_ADJ_CHECKOUTDATE');
export const setRatingDialogStatus = createAction('SHOW_RATING_DIALOG_STATUS');
export const setIsEdit = createAction('SET_ISEDIT');
export const setEditDateTime = createAction('SET_EDIT_DATE_TIME');
export const setEditDuration = createAction('SET_EDIT_DURATION');
export const setEditBreakId = createAction('SET_EDIT_BREAK_ID');
export const setPaidResponse = createAction('SET_PAID_RESPONSE');
export const resetAdminTimeheetDetails = createAction('RESET_ADMIN_TIMESHEET_DETAILS');
export const setIsFetching = createAction('SET_IS_FETCHING');

const initialState = {
	adjustedCheckInDate: '',
	adjustedCheckInTime: '',
	adjustedCheckOutDate: '',
	adjustedCheckOutTime: '',
	breaktimes: [],
	dateTime: '',
	duration: '30',
	editBreakId: '',
	editDateTime: '',
	editDuration: '30',
	isEdit: false,
	isFetching: false,
	message: '',
	notesList: [],
	paidResponse: {},
	ratingdialog: false,
	shiftSummary: {},
	timesheetDetails: {}
};

export default handleActions(
	{
		CLEAR_MESSAGES: (state) => ({ ...state, message: initialState.message }),
		RESET_ADMIN_TIMESHEET_DETAILS: () => initialState,
		SET_ADJ_CHECKINDATE: (state, action) => ({ ...state, adjustedCheckInDate: action.payload }),
		SET_ADJ_CHECKINTIME: (state, action) => ({ ...state, adjustedCheckInTime: action.payload }),
		SET_ADJ_CHECKOUTDATE: (state, action) => ({ ...state, adjustedCheckOutDate: action.payload }),
		SET_ADJ_CHECKOUTTIME: (state, action) => ({ ...state, adjustedCheckOutTime: action.payload }),
		SET_BREAK_DATETIME: (state, action) => ({ ...state, dateTime: action.payload }),
		SET_BREAK_DURATION: (state, action) => ({ ...state, duration: action.payload }),
		SET_COMMENTS: (state, action) => ({ ...state, notesList: action.payload }),
		SET_EDIT_BREAK_ID: (state, action) => ({ ...state, editBreakId: action.payload }),
		SET_EDIT_DATE_TIME: (state, action) => ({ ...state, editDateTime: action.payload }),
		SET_EDIT_DURATION: (state, action) => ({ ...state, editDuration: action.payload }),
		SET_IS_FETCHING: (state, action) => ({ ...state, isFetching: action.payload }),
		SET_ISEDIT: (state, action) => ({ ...state, isEdit: action.payload }),
		SET_MESSAGE: (state, action) => ({ ...state, message: action.payload }),
		SET_PAID_RESPONSE: (state, action) => ({ ...state, paidResponse: action.payload }),
		SET_SHIFT_BREAKTIMES: (state, action) => ({ ...state, breaktimes: action.payload }),
		SET_SHIFT_TIMESHEET_SUMMARY: (state, action) => ({ ...state, shiftSummary: action.payload }),
		SET_SUPER_TIMESHEET_DETAIL: (state, action) => ({ ...state, timesheetDetails: action.payload }),
		SHOW_RATING_DIALOG_STATUS: (state, action) => ({ ...state, ratingdialog: action.payload })
	},
	initialState
);

// Get Admin timesheet
export const getTimesheet = (id) =>
	function (dispatch) {
		const ALTERATION = 'alteration';
		let isAlteration = false;

		dispatch(setIsFetching(true));

		Agent.getSupervisorTimesheetDetail(id).then((response) => {
			if (response.ok) {
				dispatch(setSuperTimesheetDetail(response.data));
				// check if alteration is there, update checkin checkout values
				if (response.data.timesheet_componenets !== null && response.data.timesheet_componenets.length !== 0) {
					response.data.timesheet_componenets.forEach((iTime) => {
						if (iTime.component_type === ALTERATION) {
							dispatch(setAdjCheckInDate(iTime.from_date));
							dispatch(setAdjCheckOutDate(iTime.to_date));
							dispatch(setAdjCheckInTime(iTime.from_date));
							dispatch(setAdjCheckOutTime(iTime.to_date));
							isAlteration = true;
						}
					});
				}

				if (isAlteration == false) {
					dispatch(setAdjCheckInDate(response.data.checkin_time));
					dispatch(setAdjCheckOutDate(response.data.status === constants.ONGOING ? '' : response.data.checkout_time));
					dispatch(setAdjCheckInTime(response.data.checkin_time));
					dispatch(setAdjCheckOutTime(response.data.status === constants.ONGOING ? '' : response.data.checkout_time));
				}
				dispatch(setIsFetching(false));
			} else {
				dispatch(handleError(response));
				dispatch(setIsFetching(false));
			}
		});
	};

// Get admin shift timesheet summary
export const shiftTimesheetSummary = (shift_id) =>
	function (dispatch) {
		Agent.getShiftDetail(shift_id).then((response) => {
			if (response.ok) {
				dispatch(setShiftSummaryTimesheet(response.data));
			} else {
				dispatch(handleError(response));
			}
		});
	};

// get comment with timesheet id
export const getTimesheetComment = (timesheet_id) =>
	function (dispatch) {
		Agent.getTimesheetNotes(timesheet_id).then((response) => {
			if (response.ok) {
				dispatch(setComments(response.data));
			} else {
				dispatch(handleError(response));
			}
		});
	};

// add comment to the timesheet when an admin adds a break exception
export const addBreakExceptionComment = ({ timesheetID, exception, supervisorName }) =>
	function (dispatch, getState) {
		const adminName = getState().user.fullname;
		const message = `Break not taken, exception granted by: ${adminName} on behalf of ${supervisorName}, ${exception}`;
		Agent.addComments(timesheetID, message, 'default').then((response) => {
			if (response.ok) {
				dispatch(getTimesheetComment(timesheetID));
			} else {
				dispatch(handleError(response));
			}
		});
	};

// Add note api call
export const addComment = (timesheet_id, message_type) =>
	function (dispatch, getState) {
		const { message } = getState().adminTimesheetDetail;

		Agent.addComments(timesheet_id, message, message_type).then((response) => {
			if (response.ok) {
				dispatch(getTimesheetComment(timesheet_id));
				dispatch(clearMessage());
				mixpanel.track(mixpanelConstants.ADD_COMMENT, {
					commentMessage: message,
					messageType: message_type,
					timesheetId: timesheet_id
				});
			} else {
				dispatch(handleError(response));
			}
		});
	};

// Get breaktimes api call
export const fetchTimesheetBreaktimes = (id) =>
	function (dispatch) {
		Agent.getTimesheetComponents(id).then((response) => {
			if (response.ok) {
				dispatch(setShiftBreaktimes(response.data));
			} else {
				dispatch(handleError(response));
			}
		});
	};

export const approvedTimesheet = ({
	id,
	status,
	taskTotals,
	taskHours,
	mileage,
	shiftType,
	supervisorName,
	additionalPoints
}) =>
	function (dispatch, getState) {
		const {
			adjustedCheckInDate,
			adjustedCheckInTime,
			adjustedCheckOutDate,
			adjustedCheckOutTime,
			isEdit,
			timesheetDetails
		} = getState().adminTimesheetDetail;
		let checkinDateTime;
		let checkoutDateTime;
		const checkinDateTZ = dayjs(adjustedCheckInDate).format('YYYY-MM-DD');
		const checkoutDateTZ = dayjs(adjustedCheckOutDate).format('YYYY-MM-DD');

		let adjCheckInTime = adjustedCheckInTime;
		let adjCheckOutTime = adjustedCheckOutTime;

		if (isEdit === true) {
			if (adjustedCheckInTime.includes('T')) {
				adjCheckInTime = momenttz(adjustedCheckInTime).tz(timesheetDetails.timezone_name).format('HH:mm:ss');
			}
			if (adjustedCheckOutTime.includes('T')) {
				adjCheckOutTime = momenttz(adjCheckOutTime).tz(timesheetDetails.timezone_name).format('HH:mm:ss');
			}
			checkinDateTime = `${formatDate.formatEditDate('YYYY-MM-DD', checkinDateTZ)} ${adjCheckInTime}`;
			checkoutDateTime = `${formatDate.formatEditDate('YYYY-MM-DD', checkoutDateTZ)} ${adjCheckOutTime}`;
		}
		if (isEdit === false) {
			checkinDateTime = null;
			checkoutDateTime = null;
		}
		dispatch(setIsFetching(true));

		Agent.approveTimesheet(
			id,
			checkinDateTime,
			checkoutDateTime,
			status,
			supervisorName,
			mileage,
			shiftType,
			taskTotals,
			taskHours,
			additionalPoints
		).then((response) => {
			if (response.ok) {
				dispatch(getTimesheet(id));
				dispatch(fetchTimesheetBreaktimes(id));
				dispatch(setRatingDialogStatus(true));
				dispatch(setIsFetching(false));
				mixpanel.track(mixpanelConstants.APPROVE_TIMESHEET, {
					timesheetId: id,
					timesheetStatus: timesheetConstants.APPROVED
				});
			} else {
				dispatch(handleError(response));
				dispatch(setIsFetching(false));
			}
		});
	};

// Mark timesheet as paid api call
export const markTimesheetAsPaid = (id) =>
	function (dispatch, getState) {
		const {
			adjustedCheckInDate,
			adjustedCheckInTime,
			adjustedCheckOutDate,
			adjustedCheckOutTime,
			isEdit,
			timesheetDetails
		} = getState().adminTimesheetDetail;
		let checkinDateTime;
		let checkoutDateTime;
		let adjCheckInTime = adjustedCheckInTime;
		let adjCheckOutTime = adjustedCheckOutTime;

		if (isEdit === true) {
			if (adjustedCheckInTime.includes('T')) {
				adjCheckInTime = momenttz(adjustedCheckInTime).tz(timesheetDetails.timezone_name).format('HH:mm:ss');
			}
			if (adjustedCheckOutTime.includes('T')) {
				adjCheckOutTime = momenttz(adjCheckOutTime).tz(timesheetDetails.timezone_name).format('HH:mm:ss');
			}
			checkinDateTime = `${formatDate.formatEditDate('YYYY-MM-DD', adjustedCheckInDate)} ${adjCheckInTime}`;
			checkoutDateTime = `${formatDate.formatEditDate('YYYY-MM-DD', adjustedCheckOutDate)} ${adjCheckOutTime}`;
		}
		if (isEdit === false) {
			checkinDateTime = null;
			checkoutDateTime = null;
		}

		dispatch(setIsFetching(true));

		Agent.markAsPaid(id, checkinDateTime, checkoutDateTime).then((response) => {
			if (response.ok) {
				dispatch(setPaidResponse(response.data));
				dispatch(getTimesheet(id));
				dispatch(fetchTimesheetBreaktimes(id));
				// dispatch(setSuperTimesheetDetail(response.data))
				dispatch(setIsFetching(false));
				mixpanel.track(mixpanelConstants.MARK_TIMESHEET_AS_PAID, {
					timesheetId: id,
					timesheetStatus: timesheetConstants.PAID
				});
			} else {
				dispatch(handleError(response));
				dispatch(setIsFetching(false));
			}
		});
	};

export const setTimesheetToPending = (id, status) => (dispatch) => {
	dispatch(setIsFetching(true));
	Agent.updateTimesheetStatus(id, status).then((response) => {
		dispatch(setIsFetching(false));

		if (response.ok) {
			dispatch(getTimesheet(id));
		} else {
			dispatch(setOpenAlertModal(true));
			dispatch(
				setAlertMessageInfo({
					message: "Couldn't update the timesheet to pending approval",
					status: 'error'
				})
			);
		}
	});
};

// Mark timesheet as ineligible api call
export const markTimesheetAsIneligible = (id, status) =>
	function (dispatch, getState) {
		dispatch(setIsFetching(true));
		Agent.markAsIneligible(id, status).then((response) => {
			if (response.ok) {
				dispatch(getTimesheet(id));
				dispatch(fetchTimesheetBreaktimes(id));
				trackSetInligibile(status, getState());
				dispatch(setIsFetching(false));
			} else {
				dispatch(handleError(response));
				dispatch(setIsFetching(false));
			}
		});
	};

const trackSetInligibile = (status, state) => {
	const { timesheetDetails, shiftSummary } = state.adminTimesheetDetail;
	mixpanel.track(mixpanelConstants.UPDATE_TIMESHEET_STATUS, {
		department: shiftSummary.department,
		location: shiftSummary.location,
		nurse: timesheetDetails.nurse,
		organization: shiftSummary.organization,
		shiftId: timesheetDetails.shift_id,
		supervisor: shiftSummary.supervisor_name,
		timesheetId: timesheetDetails.id,
		timesheetStatus: status
	});
};

export const resetAdminTimesheetDetailVal = () =>
	function (dispatch) {
		dispatch(setMessage(''));
	};

export const saveEditedTimesheet = ({
	id,
	taskTotals,
	taskHours,
	mileage,
	shiftType,
	supervisorName,
	additionalPoints
}) =>
	function (dispatch, getState) {
		const {
			adjustedCheckInDate,
			adjustedCheckInTime,
			adjustedCheckOutDate,
			adjustedCheckOutTime,
			message,
			timesheetDetails
		} = getState().adminTimesheetDetail;
		let checkinDateTime;
		let checkoutDateTime;
		let adjCheckInTime = adjustedCheckInTime;
		let adjCheckOutTime = adjustedCheckOutTime;
		let adjCheckInDate = adjustedCheckInDate;
		let adjCheckOutDate = adjustedCheckOutDate;

		if (adjustedCheckInTime.includes('T')) {
			adjCheckInTime = momenttz(adjustedCheckInTime).tz(timesheetDetails.timezone_name).format('HH:mm:ss');
		}
		if (adjustedCheckOutTime.includes('T')) {
			adjCheckOutTime = momenttz(adjustedCheckOutTime).tz(timesheetDetails.timezone_name).format('HH:mm:ss');
		}
		if (typeof adjustedCheckInDate === 'string') {
			adjCheckInDate = momenttz(adjustedCheckInDate).tz(timesheetDetails.timezone_name);
		}
		if (typeof adjustedCheckOutDate === 'string') {
			adjCheckOutDate = momenttz(adjustedCheckOutDate).tz(timesheetDetails.timezone_name);
		}
		checkinDateTime = `${formatDate.formatEditDate('YYYY-MM-DD', adjCheckInDate)} ${adjCheckInTime}`;
		checkoutDateTime = `${formatDate.formatEditDate('YYYY-MM-DD', adjCheckOutDate)} ${adjCheckOutTime}`;

		if (adjustedCheckOutTime === '') {
			checkoutDateTime = '';
		}

		Agent.saveEditedTimesheet(
			id,
			checkoutDateTime,
			checkinDateTime,
			message,
			supervisorName,
			mileage,
			shiftType,
			taskTotals,
			taskHours,
			additionalPoints
		).then((response) => {
			if (response.ok) {
				dispatch(getTimesheet(id));
				dispatch(getTimesheetComment(id));
			} else {
				alert(response.data);
			}
		});
	};
